'use client'

import * as React from 'react'
import { zodResolver } from '@hookform/resolvers/zod'
import { useForm } from 'react-hook-form'
import type { NewsletterSubscriptionInput } from '@/lib/forms/schemas'
import { newsletterSubscriptionSchema } from '@/lib/forms/schemas'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { FormStatus } from '@/components/forms/form-status'

type ApiResponse =
  | { ok: true; message: string; warning?: string; fieldErrors?: Record<string, string[]> }
  | { ok: false; message: string; fieldErrors?: Record<string, string[]> }

type NewsletterFormProps = Readonly<{
  compact?: boolean
  source?: string
}>

export function NewsletterForm({ compact = false, source = 'footer' }: NewsletterFormProps) {
  const [startedAt] = React.useState(() => new Date().toISOString())
  const [status, setStatus] = React.useState<{ kind: 'idle' | 'success' | 'error'; title?: string; description?: string }>({ kind: 'idle' })

  const form = useForm<NewsletterSubscriptionInput>({
    resolver: zodResolver(newsletterSubscriptionSchema),
    defaultValues: {
      email: '',
      honeypot: '',
      startedAt,
      source,
    },
    mode: 'onBlur',
  })

  const onSubmit = form.handleSubmit(async values => {
    setStatus({ kind: 'idle' })
    form.clearErrors()

    const response = await fetch('/api/newsletter', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(values),
    })

    const data = (await response.json().catch(() => null)) as ApiResponse | null

    if (!response.ok || !data || !data.ok) {
      if (data && !data.ok && data.fieldErrors?.email?.length) {
        form.setError('email', { type: 'server', message: data.fieldErrors.email[0] })
      }

      setStatus({
        kind: 'error',
        title: 'Subscription failed',
        description: data?.message ?? 'Please try again.',
      })
      return
    }

    form.reset({
      email: '',
      honeypot: '',
      startedAt: new Date().toISOString(),
      source,
    })

    setStatus({
      kind: 'success',
      title: 'Subscribed',
      description: data.warning ?? data.message,
    })
  })

  if (compact) {
    return (
      <form className="grid gap-3" onSubmit={onSubmit}>
        <input type="hidden" {...form.register('honeypot')} aria-hidden="true" tabIndex={-1} autoComplete="off" />
        <input type="hidden" {...form.register('startedAt')} />
        <input type="hidden" {...form.register('source')} />
        <FormStatus kind={status.kind} title={status.title} description={status.description} />
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="grid flex-1 gap-2">
            <Label htmlFor="newsletter-email" className="sr-only">
              Email
            </Label>
            <Input id="newsletter-email" type="email" autoComplete="email" placeholder="Email address" {...form.register('email')} />
          </div>
          <Button type="submit" disabled={form.formState.isSubmitting}>
            {form.formState.isSubmitting ? 'Joining...' : 'Join'}
          </Button>
        </div>
      </form>
    )
  }

  return (
    <form className="grid gap-4" onSubmit={onSubmit}>
      <input type="hidden" {...form.register('honeypot')} aria-hidden="true" tabIndex={-1} autoComplete="off" />
      <input type="hidden" {...form.register('startedAt')} />
      <input type="hidden" {...form.register('source')} />
      <FormStatus kind={status.kind} title={status.title} description={status.description} />
      <div className="grid gap-2">
        <Label htmlFor="newsletter-email-full">Email</Label>
        <Input id="newsletter-email-full" type="email" autoComplete="email" placeholder="you@example.com" {...form.register('email')} />
        {form.formState.errors.email ? <p className="text-sm text-destructive">{form.formState.errors.email.message}</p> : null}
      </div>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
        <Button type="submit" disabled={form.formState.isSubmitting}>
          {form.formState.isSubmitting ? 'Subscribing...' : 'Subscribe'}
        </Button>
        <p className="text-sm leading-6 text-muted-foreground">New product notes and release updates. No spam.</p>
      </div>
    </form>
  )
}
